import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  ApiService,
  CampaignType,
  IChannels,
  ISegments,
  ITactics,
  IVCampaign,
  UserType
} from '@libs/apis';
import { CommonAuthService } from '@libs/common-services';
import { BehaviorSubject } from 'rxjs';
import { SharedDataService } from '../../services/shared-data.service';
import {
  CampaignDimension,
  TaxonomyUiConstants
} from '../../taxonomy-ui/taxonomy-ui.constants';
import { TaxonomyHelper } from '../../helper/taxonomy-helper';
import { TaxonomyFeature } from '../../models/TaxonomyFeature';

@Component({
  selector: 'lib-taxonomy-campaign-mapping-update-popover',
  templateUrl: './campaign-mapping-update-popover.component.html',
  styleUrls: ['./campaign-mapping-update-popover.component.scss'],
  animations: TaxonomyUiConstants.updateTacticOverlayTriggerAnimations,
  standalone: false
})
export class CampaignMappingUpdatePopoverComponent
  implements OnInit, OnChanges
{
  private static readonly geoTestMessage = `(Geo Test in progress)`;

  @Input()
  totalCampaigns = 0;

  @Input()
  selectedCampaignType = '';

  @Input()
  filter: Record<string, string> = {};

  @Input()
  selectedDataSource = '';

  @Input()
  selectedCampaignCount = 0;

  @Input()
  selectedDimensionOptions: Record<CampaignDimension, string[]> = {
    [CampaignDimension.channel]: [],
    [CampaignDimension.tactic]: [],
    [CampaignDimension.segment]: []
  };

  @Input()
  public clientId = 0;

  @Input()
  public brandId = 0;

  @Input()
  public layout = 'default';

  @Input()
  public actionStep: 'choice' | 'override' = 'choice';

  @Output() cancelDimensionMapping = new EventEmitter();

  @Output() updateDimensionMapping = new EventEmitter();

  @Output() applyAIRecommendationsMapping = new EventEmitter();

  @Output() actionStepChange = new EventEmitter<string>();

  public loading = new BehaviorSubject<boolean>(false);
  public tactics: ITactics[] = [];
  public channels: IChannels[] = [];
  public segments: ISegments[] = [];
  public campaigns: IVCampaign[] = [];
  public geoTestStatusByDimension: Record<string, boolean> = {};
  public isGeoTestRunning = false;
  public campaignDimension = CampaignDimension;
  public updateDimensionValue: Record<CampaignDimension, string> = {
    [CampaignDimension.channel]: '',
    [CampaignDimension.tactic]: '',
    [CampaignDimension.segment]: ''
  };

  constructor(
    private readonly apiService: ApiService,
    private readonly sharedDataService: SharedDataService,
    private readonly authService: CommonAuthService
  ) {}

  ngOnInit(): void {
    this.fetchDimensionValues();
    this.getGeoTestStatus();
    this.prepareStateIfAiRecommendationsEnabled();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['actionStep']?.currentValue === 'choice') {
      this.updateDimensionValue = {
        [CampaignDimension.channel]: '',
        [CampaignDimension.tactic]: '',
        [CampaignDimension.segment]: ''
      };
    }
  }

  private fetchDimensionValues() {
    this.loading.next(true);
    const selectedDataSource = encodeURIComponent(this.selectedDataSource);
    Promise.all([
      this.apiService.taxonomyApisOperations.getTacticsByDatasource(
        this.clientId,
        this.brandId,
        /* PMDEV-7503: We're not sending the data source because
         * AI recommended campaigns might be grouped by
         * dimension other than the composite data source
         * and, some data sources may not have any tactics
         * within that data source to show a list, so we need to show ALL the tactics.
         */
        undefined
      ),
      this.apiService.taxonomyApisOperations.getSegments(
        this.clientId,
        this.brandId,
        selectedDataSource
      ),
      this.apiService.taxonomyApisOperations.getChannels(
        this.clientId,
        this.brandId,
        selectedDataSource
      )
    ])
      .then(([tactics, segments, channels]) => {
        this.channels = this.removeGroupIfItemsAreEmpty(
          channels
        ) as IChannels[];
        this.tactics = this.removeGroupIfItemsAreEmpty(tactics) as ITactics[];
        this.segments = this.removeGroupIfItemsAreEmpty(
          segments
        ) as ISegments[];
        this.updateTacticsWithGeoTestStatus();
        this.loading.next(false);
      })
      .catch((error) => {
        console.error('Error: ', error.message);
        this.loading.next(false);
      });
  }

  abortTacticUpdate() {
    this.cancelDimensionMapping?.emit();
  }

  handleGeoTestMessageVisibility() {
    this.isGeoTestRunning =
      Object.values(this.updateDimensionValue).findIndex((dimensionValue) =>
        dimensionValue?.includes(
          CampaignMappingUpdatePopoverComponent.geoTestMessage
        )
      ) >= 0;
  }

  applyAIRecommendations() {
    this.applyAIRecommendationsMapping.emit({
      action: 'approve',
      ...this.updateDimensionValue
    });
  }

  editMapping() {
    this.changeActionStep('override');
  }

  unmapCampaigns() {
    this.updateDimensionMapping?.emit({
      channel: TaxonomyUiConstants.unmappedValue,
      tactic: TaxonomyUiConstants.unmappedValue,
      segment: TaxonomyUiConstants.unmappedValue,
      action: 'unmap'
    });
  }

  updateDimension() {
    this.removeGeoTestMessageIfPresent();
    this.updateDimensionMapping?.emit({
      action: 'edit',
      ...this.updateDimensionValue
    });
  }

  confirmToResetMapping() {
    console.warn('confirmToResetMapping');
  }

  private getGeoTestStatus() {
    this.sharedDataService
      .getGeoTestStatusByDimension()
      .subscribe((geoTestStatus) => {
        this.geoTestStatusByDimension = geoTestStatus;
      });
  }

  private prepareStateIfAiRecommendationsEnabled() {
    const user = this.authService.getUser();
    this.actionStep =
      (user?.getUserType() || '') === UserType.external ? 'override' : 'choice';
    this.changeActionStep(this.actionStep);
  }

  private updateTacticsWithGeoTestStatus() {
    this.tactics.forEach((tacticGroup) => {
      tacticGroup.items.forEach((tactic) => {
        if (this.geoTestStatusByDimension[tactic.tactic || '']) {
          tactic.tactic = `${tactic.tactic} ${CampaignMappingUpdatePopoverComponent.geoTestMessage}`;
        }
      });
    });
  }

  private removeGeoTestMessageIfPresent() {
    Object.values(CampaignDimension).forEach((dimension) => {
      if (
        this.updateDimensionValue[dimension]?.includes(
          CampaignMappingUpdatePopoverComponent.geoTestMessage
        )
      ) {
        this.updateDimensionValue[dimension] = this.updateDimensionValue[
          dimension
        ]
          .replace(
            new RegExp(
              `\\(${CampaignMappingUpdatePopoverComponent.geoTestMessage}\\)`,
              'g'
            ),
            ''
          )
          .trim();
      }
    });
  }

  setValue(dimension: CampaignDimension, event: { value: string }) {
    this.updateDimensionValue[dimension] = event.value || '';
  }

  goBack() {
    this.changeActionStep('choice');
  }

  abortTacticUpdateAndGoBack() {
    this.updateDimensionValue = {
      [CampaignDimension.channel]: '',
      [CampaignDimension.tactic]: '',
      [CampaignDimension.segment]: ''
    };
    this.abortTacticUpdate();
    this.goBack();
  }

  changeActionStep(step: 'choice' | 'override') {
    this.actionStep = step;
    if (this.actionStepChange) {
      this.actionStepChange.emit(step);
    }
  }

  private removeGroupIfItemsAreEmpty(
    options: ITactics[] | IChannels[] | ISegments[]
  ) {
    return options.filter((optionGroup) => optionGroup.items.length > 0);
  }

  public getConfirmationMessage(): string {
    switch (this.selectedCampaignType) {
      case TaxonomyFeature.AI:
      case CampaignType.UNMAPPED_CAMPAIGN:
        if (this.actionStep === 'choice') {
          if (this.selectedCampaignCount === this.totalCampaigns) {
            return 'Edit or approve all selected campaigns';
          } else if (this.selectedCampaignCount > 0) {
            return `Edit or approve ${this.selectedCampaignCount} selected ${TaxonomyHelper.getCampaignString(this.selectedCampaignCount)}`;
          } else {
            return 'Select campaigns to edit or approve their mapping.';
          }
        } else {
          if (this.selectedCampaignCount === this.totalCampaigns) {
            return 'Change mapping for all selected campaigns';
          } else {
            return `Change mapping for ${this.selectedCampaignCount} ${TaxonomyHelper.getCampaignString(this.selectedCampaignCount)}`;
          }
        }
      case CampaignType.MAPPED_CAMPAIGN:
      case CampaignType.UNDER_PROCESSING_CAMPAIGN:
        if (this.actionStep === 'choice') {
          if (this.selectedCampaignCount === this.totalCampaigns) {
            return 'Edit or unmap all selected campaigns';
          } else if (this.selectedCampaignCount > 0) {
            return `Edit or unmap  ${this.selectedCampaignCount} ${TaxonomyHelper.getCampaignString(this.selectedCampaignCount)}`;
          } else {
            return 'Select campaigns to edit their mapping.';
          }
        } else {
          if (this.selectedCampaignCount === this.totalCampaigns) {
            return 'Edit mapping for all selected campaigns';
          } else {
            return `Edit mapping for ${this.selectedCampaignCount} ${TaxonomyHelper.getCampaignString(this.selectedCampaignCount)}`;
          }
        }
      default:
        return '';
    }
  }
}
